import React, { useState, useMemo } from 'react';
import { PageHeader, SectionHeader } from '../components/SectionHeader';
import { glossaryTerms } from '../data/nflData';
import { Search, BookOpen } from 'lucide-react';
import { Input } from '../components/ui/input';

const Glossary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('');

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  
  // Get available letters (letters that have terms)
  const availableLetters = useMemo(() => {
    return [...new Set(glossaryTerms.map(term => term.term[0].toUpperCase()))];
  }, []);

  // Filter terms based on search and letter
  const filteredTerms = useMemo(() => {
    return glossaryTerms.filter(term => {
      const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           term.definition.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLetter = selectedLetter === '' || term.term[0].toUpperCase() === selectedLetter;
      return matchesSearch && matchesLetter;
    });
  }, [searchTerm, selectedLetter]);

  // Group terms by letter
  const groupedTerms = useMemo(() => {
    const groups = {};
    filteredTerms.forEach(term => {
      const letter = term.term[0].toUpperCase();
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(term);
    });
    return groups;
  }, [filteredTerms]);

  return (
    <div className="min-h-screen" data-testid="glossary-page">
      <PageHeader 
        title="Glosario NFL"
        subtitle="Diccionario completo de términos del fútbol americano"
        backgroundImage="https://images.unsplash.com/photo-1547992454-bec8143cc790?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Search */}
        <div className="sticky top-20 z-30 bg-slate-950/95 backdrop-blur-xl py-6 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 mb-8 border-b border-slate-800">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                type="text"
                placeholder="Buscar término..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-12 text-lg"
                data-testid="glossary-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 text-sm">
                {filteredTerms.length} términos
              </span>
              {(searchTerm || selectedLetter) && (
                <button
                  onClick={() => { setSearchTerm(''); setSelectedLetter(''); }}
                  className="text-emerald-400 text-sm hover:underline"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
          
          {/* Alphabet filter */}
          <div className="flex flex-wrap gap-1 mt-4">
            <button
              onClick={() => setSelectedLetter('')}
              className={`w-8 h-8 rounded text-sm font-bold transition-colors ${
                selectedLetter === '' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              All
            </button>
            {alphabet.map(letter => (
              <button
                key={letter}
                onClick={() => setSelectedLetter(letter === selectedLetter ? '' : letter)}
                disabled={!availableLetters.includes(letter)}
                className={`w-8 h-8 rounded text-sm font-bold transition-colors ${
                  selectedLetter === letter 
                    ? 'bg-emerald-500 text-white' 
                    : availableLetters.includes(letter)
                      ? 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      : 'bg-slate-900 text-slate-700 cursor-not-allowed'
                }`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Terms list */}
        <div className="space-y-12">
          {Object.keys(groupedTerms).sort().map(letter => (
            <div key={letter}>
              <div className="flex items-center gap-4 mb-6">
                <span 
                  className="text-5xl font-black text-emerald-500"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {letter}
                </span>
                <div className="flex-1 h-px bg-slate-800" />
              </div>
              
              <div className="space-y-4">
                {groupedTerms[letter].map((item, index) => (
                  <div 
                    key={index}
                    className="glossary-term group hover:bg-slate-800/30 p-4 rounded-lg -mx-4 transition-colors"
                    data-testid={`glossary-term-${item.term.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="term-name group-hover:text-emerald-300 transition-colors">
                      {item.term}
                    </span>
                    <span className="term-definition">{item.definition}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {filteredTerms.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h3 
                className="text-xl font-bold text-white mb-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                No se encontraron términos
              </h3>
              <p className="text-slate-500">
                Prueba con otra búsqueda o quita los filtros
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Glossary;
